// ******************************************************************************
// Schema
// ******************************************************************************

import type { RelativeDateComparisonValue } from "../branchRuleDateComparison";

export interface PortalSettingsSchema {
  formStepId: string;
  questionElementId: string;
  inputFieldId: string;
  firstNameFormStepId?: string;
  firstNameQuestionElementId?: string;
  firstNameInputFieldId?: string;
  lastNameFormStepId?: string;
  lastNameQuestionElementId?: string;
  lastNameInputFieldId?: string;
  phoneNumberFormStepId?: string;
  phoneNumberQuestionElementId?: string;
  phoneNumberInputFieldId?: string;
  sendSignupInviteEmail?: boolean;
  signupInviteEmailIntro?: string;
}

export type JourneyChannelSchema = "SelfServe" | "Internal";

export type BackgroundPipelineTriggerKind = "manual";
export type BackgroundPipelineTriggerPlacement = "staffEnquirySettings";
export type BackgroundPipelineTriggerSyncRole = "create" | "update";

export interface BackgroundPipelineTriggerSchema {
  id: string;
  backgroundPipelineStepId: string;
  kind: BackgroundPipelineTriggerKind;
  label?: string;
  placements?: BackgroundPipelineTriggerPlacement[];
  /** When set, Save-to-CMS picks create (no externalRefs) vs update (has externalRefs). */
  syncRole?: BackgroundPipelineTriggerSyncRole;
}

export interface JourneySchema {
  id: string;
  displayId: string;
  created: string;
  organisationId: string;
  caseTypeId?: string;
  channel?: JourneyChannelSchema;
  title: string;
  steps: StepSchema[];
  codeSnippets?: CodeSnippetSchema[];
  stepPipeline: Pipe[];
  backgroundPipelineTriggers?: BackgroundPipelineTriggerSchema[];
  globalFeatures?: GlobalFeatureSchema[];
  portalSettings?: PortalSettingsSchema;
  hasError?: boolean;
  errorMessage?: string;
}

export type GlobalFeatureSchema = DialpadCallbackGlobalFeatureSchema;

export interface DialpadCallbackGlobalFeatureSchema {
  displayId: string;
  type: "DialpadCallback";
  config: {
    phoneNumberFieldPath: string;
    callCenterId?: string;
  };
}

export interface JourneyConfig {
  title: string;
  displayProgressBar: boolean;
}

// ******************************************************************************
// Steps
// ******************************************************************************

export enum StepType {
  Form = "Form",
  Content = "Content",
  Final = "Final",
  FinalSignup = "FinalSignup",
  Kickout = "Kickout",
  EndUserDataStorage = "EndUserDataStorage",
  ApiIntegration = "ApiIntegration",
  BackgroundPipeline = "BackgroundPipeline",
}

export interface Step {
  id: string;
  type: StepType;
  title: string;
  created: string;
  displayId: string;
  description: string;
}

export type StepSchema =
  | FormStepSchema
  | FormDataStorageStep
  | ContentStepSchema
  | FinalStepSchema
  | FinalSignupStepSchema
  | KickoutStepSchema
  | ApiIntegrationStepSchema
  | BackgroundPipelineStepSchema;

export interface FormStepSchema extends Step {
  type: StepType.Form;
  elements: ElementSchema[];
  formPipeline: Pipe[];
  description: string;
}

export interface FormDataStorageStep extends Step {
  type: StepType.EndUserDataStorage;
}

export interface ContentStepSchema extends Step {
  type: StepType.Content;
  description: string;
  bodyContentRevisionId?: string;
}

export interface FinalStepSchema extends Step {
  type: StepType.Final;
  description: string;
}

export interface FinalSignupStepSchema extends Step {
  type: StepType.FinalSignup;
  description: string;
}

export interface KickoutStepSchema extends Step {
  type: StepType.Kickout;
  description: string;
  phoneNumber?: string;
}

export interface StoredCode {
  systemStartLines: string[];
  userCodeLines: string[];
  systemEndLines: string[];
}

export interface OutputVariable {
  name: string;
  type: string;
}

export enum ApiIntegrationRequestContentType {
  Json = "Json",
  MultipartFormData = "MultipartFormData",
}

export enum ApiIntegrationResponseContentType {
  Json = "Json",
  PlainText = "PlainText",
}

export interface ApiIntegrationConfig {
  apiRegistrationId: string;
  /** Integration vendor when added as a plugin step (backend identifier, e.g. "sharedo" — displayed as "Clio Operate"). */
  vendor?: string;
  /** Preset endpoint id from the vendor's catalog; undefined for custom endpoints. */
  presetEndpointId?: string;
  relativeApiPath: string;
  method: string;
  headers: Record<string, string>;
  outputVariables: OutputVariable[];
  requestContentType: ApiIntegrationRequestContentType;
  responseContentType?: ApiIntegrationResponseContentType;
  useDynamicPath?: boolean;
  issues?: Record<string, string[]>;
  mappingCode?: StoredCode;
  sourceContentRevisions?: Record<string, string>;
}

export interface ApiIntegrationStepSchema extends Step {
  type: StepType.ApiIntegration;
  dataSinkConfig: ApiIntegrationConfig;
  description: string;
}

export interface CodeSnippetSchema {
  id: string;
  title: string;
  displayId: string;
  sourceContentRevisionId?: string;
  organisationSnippetId?: string;
  caseTypeSnippetId?: string;
}

export interface CaseTypeCodeSnippetSchema {
  id: string;
  created: string;
  organisationId: string;
  caseTypeId: string;
  title: string;
  displayId: string;
  sourceContentRevisionId?: string;
}

export interface CaseTypeSchema {
  id: string;
  created: string;
  organisationId: string;
  title: string;
  displayId: string;
}

export interface OrganisationCodeSnippetSchema {
  id: string;
  created: string;
  organisationId: string;
  title: string;
  displayId: string;
  sourceContentRevisionId?: string;
}

export interface BackgroundPipelineStepSchema extends Step {
  type: StepType.BackgroundPipeline;
  pipeline: Pipe[];
}

export enum ElementType {
  Question = "Question",
  Content = "Content",
  Integration = "Integration",
  Script = "Script",
}

export enum IntegrationType {
  GoogleAddressSearch = "GoogleAddressSearch",
  GooglePlaceSearch = "GooglePlaceSearch",
  VehicleSearch = "VehicleSearch",
  InsuranceSearch = "InsuranceSearch",
  VonageVerifyEmailEntry = "VonageVerifyEmailEntry",
  VonageVerifyCodeEntry = "VonageVerifyCodeEntry",
  VonageVerifyInline = "VonageVerifyInline",
  SharedoOdsSearch = "SharedoOdsSearch",
}

export type ElementSchema = QuestionElementSchema | ContentElementSchema | IntegrationElementSchema | ScriptElementSchema;

// ******************************************************************************
// Questions
// ******************************************************************************

export interface QuestionElementSchema {
  type: ElementType.Question;
  id: string;
  displayId: string;
  created: string;
  title: string;
  fields: FieldSchema[];
}

export interface ContentElementSchema {
  type: ElementType.Content;
  id: string;
  displayId: string;
  created: string;
  title: string;
  bodyContentRevisionId?: string;
}

export interface IntegrationElementSchema {
  type: ElementType.Integration;
  id: string;
  displayId: string;
  created: string;
  title: string;
  integrationType: IntegrationType;
  config?: Record<string, any>;
}

export interface ScriptElementSchema {
  type: ElementType.Script;
  id: string;
  displayId: string;
  created: string;
  title: string;
  scriptText?: string;
}

// *****************************************************************************
// Pipeline
// *****************************************************************************

export enum PipeType {
  Standard = "Standard",
  ElementRepeater = "ElementRepeater",
  Start = "Start",
}

export enum ComparisonType {
  Equals = "Equals",
  NotEquals = "NotEquals",
  Contains = "Contains",
  GreaterThan = "GreaterThan",
  LessThan = "LessThan",
}

export interface StandardPipe {
  id: string;
  type: PipeType.Standard;
  targetId: string;
  positionX: number;
  positionY: number;
  junctions: Junction[];
}

export interface ElementRepeaterPipe {
  id: string;
  type: PipeType.ElementRepeater;
  positionX: number;
  positionY: number;
  junctions: Junction[];
  displayId?: string;
  title: string;
  minItems: number;
  maxItems: number; // 0 = unlimited
  addButtonText: string;
  removeButtonText: string;
  /** Nested form pipeline walked per row. */
  pipeline: Pipe[];
  hideElementTitles?: boolean;
}

export interface StartPipe {
  id: string;
  type: PipeType.Start;
  positionX: number;
  positionY: number;
  junctions: Junction[];
}

export type Pipe = StandardPipe | ElementRepeaterPipe | StartPipe;

export enum JunctionType {
  Direct = "Direct",
  Branch = "Branch",
  Background = "Background",
}

export interface Junction {
  id: string;
  type: JunctionType;
  targetPipeId: string;
}

export interface DirectJunction extends Junction {
  type: JunctionType.Direct;
}

export interface BranchJunction extends Junction {
  type: JunctionType.Branch;
  rules: BranchRule[];
}

export interface BackgroundJunction extends Junction {
  type: JunctionType.Background;
}

export enum ComparisonValueType {
  String = "String",
  Decimal = "Decimal",
  Boolean = "Boolean",
  Integer = "Integer",
  Date = "Date",
  StringArray = "StringArray",
}

export interface BranchRule {
  id: string;
  sourceQuestionId: string;
  sourceFieldId: string;
  comparisonValueType: ComparisonValueType;
  comparisonType: ComparisonType;
  comparisonValue: string | number | boolean | string[] | RelativeDateComparisonValue | null;
}

// ******************************************************************************
// Fields
// ******************************************************************************

export type FieldSchema = InputFieldSchema | ContentFieldSchema;

export enum InputTypes {
  Text = "Text",
  Radio = "Radio",
  Checkbox = "Checkbox",
  Dropdown = "Dropdown",
  Number = "Number",
  Date = "Date",
  Time = "Time",
  Money = "Money",
  AddressSearch = "AddressSearch",
  Email = "Email",
  Phone = "Phone",
  Rating = "Rating",
  YesNo = "YesNo",
  FileUpload = "FileUpload",
}

export enum FieldType {
  Input = "Input",
  Content = "Content",
}

export interface InputFieldSchema<T = any> {
  id: string;
  type: FieldType.Input;
  created: string;
  elementId: string;
  isRequired: boolean;
  name: string;
  label?: string;
  validators?: ValidationSchema[];
  inputType: InputTypes;
  config: T;
}

// Text input

export interface TextInputFieldSchema extends InputFieldSchema<TextInputFieldConfig> {
  inputType: InputTypes.Text;
}

export interface TextInputFieldConfig {
  placeholder?: string;
  defaultValue?: string;
  multiline: boolean;
  helperText?: string;
}

// Date input

export interface DateInputFieldSchema extends InputFieldSchema<DateInputFieldConfig> {
  inputType: InputTypes.Date;
}

export interface DateInputFieldConfig {
  placeholder?: string;
  defaultValue?: string;
  minDate?: Date;
  maxDate?: Date;
  mustBeInFuture?: boolean;
  mustBeInPast?: boolean;
  minAge?: number;
  maxAge?: number;
  helperText?: string;
  dateFormat?: "US" | "GB";
}

// Dropdown input

export interface DropdownInputFieldSchema extends InputFieldSchema<DropdownInputFieldConfig> {
  inputType: InputTypes.Dropdown;
}

export interface DropdownInputFieldConfig {
  options?: OptionSchema[];
  optionSets?: OptionSetSchema[];
  placeholder?: string;
  helperText?: string;
  //optionsDependency?: OptionDependencySchema[]; // TODO: Add this later
}

// Radio input

export interface RadioInputFieldSchema extends InputFieldSchema<RadioInputFieldConfig> {
  inputType: InputTypes.Radio;
}

export interface RadioInputFieldConfig {
  options?: OptionSchema[];
  optionSets?: OptionSetSchema[];
  helperText?: string;
  //optionsDependency?: OptionDependencySchema[];
}

// YesNo input

export interface YesNoInputFieldSchema extends InputFieldSchema<YesNoInputFieldConfig> {
  inputType: InputTypes.YesNo;
}

export interface YesNoInputFieldConfig {
  defaultValue?: "yes" | "no";
  helperText?: string;
}

// Checkbox input

export interface CheckboxInputFieldSchema extends InputFieldSchema<CheckboxInputFieldConfig> {
  inputType: InputTypes.Checkbox;
}

export interface CheckboxInputFieldConfig {
  options?: OptionSchema[];
  optionSets?: OptionSetSchema[];
  helperText?: string;
  //optionsDependency?: OptionDependencySchema[];
}

// Money input

export interface MoneyInputFieldSchema extends InputFieldSchema<MoneyInputFieldConfig> {
  inputType: InputTypes.Money;
}

export interface MoneyInputFieldConfig {
  currencyCode: string;
  decimalPlaces: number;
  placeholder?: string;
  helperText?: string;
}

// Number input

export interface NumberInputFieldSchema extends InputFieldSchema<NumberInputFieldConfig> {
  inputType: InputTypes.Number;
  placeholder?: string;
}

export interface NumberInputFieldConfig {
  subType: "float" | "integer";
  defaultValue?: number;
  placeholder?: string;
  min?: number;
  max?: number;
  helperText?: string;
}

// Email input

export interface EmailInputFieldSchema extends InputFieldSchema<EmailInputFieldConfig> {
  inputType: InputTypes.Email;
}

export interface EmailInputFieldConfig {
  placeholder?: string;
  defaultValue?: string;
  helperText?: string;
}

// Phone input

export interface PhoneInputFieldSchema extends InputFieldSchema<PhoneInputFieldConfig> {
  inputType: InputTypes.Phone;
}

export interface PhoneInputFieldConfig {
  placeholder?: string;
  defaultValue?: string;
  defaultCountry?: string;
  international?: boolean;
  helperText?: string;
}

// Time input

export interface TimeInputFieldSchema extends InputFieldSchema<TimeInputFieldConfig> {
  inputType: InputTypes.Time;
}

export interface TimeInputFieldConfig {
  showSeconds?: boolean;
  placeholder?: string;
  defaultValue?: string;
  helperText?: string;
}

// Rating input

export interface RatingInputFieldSchema extends InputFieldSchema<RatingInputFieldConfig> {
  inputType: InputTypes.Rating;
}

export interface RatingInputFieldConfig {
  maxStars?: number;
  defaultValue?: number;
  helperText?: string;
}

// FileUpload input

export interface FileUploadInputFieldSchema extends InputFieldSchema<FileUploadInputFieldConfig> {
  inputType: InputTypes.FileUpload;
}

export interface FileUploadInputFieldConfig {
  multiple?: boolean;
  accept?: string;
  maxSize?: number; // in MB
  defaultValue?: string[];
  helperText?: string;
}

/* Complex functionality fields */

export interface AddressSearchQuestionSchema extends InputFieldSchema<AddressSearchQuestionConfig> {
  inputType: InputTypes.AddressSearch;
}

export interface AddressSearchQuestionConfig {
  includeCountries: string[];
  helperText?: string;
  addressFieldsRequired?: boolean;
}

/** Fields that don"t require user input. Can either have static content or dynamic content. */
export interface ContentFieldSchema {
  id: string;
  elementId: string;
  type: FieldType.Content;
  config: ContentFieldConfig;
}

export enum AlertVariant {
  Default = "Default",
  Destructive = "Destructive",
  Warning = "Warning",
  Info = "Info",
  Success = "Success",
}

export interface ContentFieldConfig {
  alertVariant: AlertVariant;
  icon?: string; // Lucide icon name
  title?: string; // Alert title
  content?: string;
}

/** Use this type when we want to change content dynamically based on answers to other questions */
export interface DynamicContentSchema {
  type: "dynamic-content";
  questionId: string;
  contents: {
    comparisonType: ComparisonType;
    value: string | string[];
    content: string;
  }[];
}

export interface ValidationSchema {
  type: "regex" | "min" | "max" | "minLength" | "maxLength";
  value: any;
  message: string;
}

export interface OptionSchema {
  label: string;
  value: string;
  isDefault?: boolean;
}

export interface OptionSetSchema {
  id: string;
  name: string;
  options: OptionSchema[];
}
